import { Link, Head, usePage } from '@inertiajs/react'
import {
  ArrowRight,
  BadgeCheck,
  Bell,
  Briefcase,
  Building2,
  Calendar,
  Heart,
  Receipt,
  Search,
  Ticket,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import PhilanthropistListPreview from '@/components/home/PhilanthropistListPreview'
import type { SharedProps } from '@/types'

const benefits = [
  {
    icon: Search,
    title: 'Discover meaningful events',
    description:
      'Browse a curated calendar of charitable galas, fundraisers, and benefits in your city — all in one place.',
  },
  {
    icon: Heart,
    title: 'Support causes you care about',
    description:
      'Filter events by cause area to find events aligned with the missions and organizations that matter most to you.',
  },
  {
    icon: Ticket,
    title: 'Secure tickets in seconds',
    description:
      'Buy tickets, reserve tables, and manage your RSVPs with a streamlined checkout — no more juggling forms or waitlists.',
  },
  {
    icon: Receipt,
    title: 'Track tax-deductible giving',
    description:
      'Keep a clean record of your contributions with itemized receipts ready for tax season — no manual bookkeeping.',
  },
  {
    icon: Users,
    title: 'Connect with like-minded peers',
    description:
      'Follow hosts, see who else is attending, and grow a network of philanthropists, founders, and changemakers.',
  },
  {
    icon: Briefcase,
    title: 'Join professional networks',
    description:
      'Select the professional networks you belong to — from industry guilds to alumni circles — and discover the galas your peers are attending.',
  },
  {
    icon: Bell,
    title: 'Get notified when you can help',
    description:
      'Receive timely alerts about event needs, donation requests, and volunteer opportunities from the organizations you follow.',
  },
  {
    icon: BadgeCheck,
    title: 'Vetted, verified hosts',
    description:
      'Every featured organization on Gala Season is verified, so you can give with confidence and avoid the noise.',
  },
]

const features = [
  {
    title: 'Personalized recommendations',
    description:
      'Get event suggestions tailored to your causes, your city, and the organizations you already support.',
  },
  {
    title: 'A single ticket wallet',
    description:
      'Every ticket you purchase lives in your account — accessible from any device, the night of the event.',
  },
  {
    title: 'Calendar integration',
    description:
      'Sync events to Google, Apple, or Outlook calendars in one click so nothing slips through the cracks.',
  },
  {
    title: 'Save events for later',
    description:
      'Bookmark galas you’re interested in and get reminded when ticket sales open or seats run low.',
  },
  {
    title: 'Public profile, on your terms',
    description:
      'Showcase the causes and organizations you champion — or stay private. You control what is visible.',
  },
  {
    title: 'Priority access to featured events',
    description:
      'Get early notice on flagship events from leading nonprofits before tickets reach the general public.',
  },
]

export default function PhilanthropistsShow() {
  const { app_url } = usePage<SharedProps>().props
  const title = 'For Philanthropists — Gala Season'
  const description =
    'Discover, attend, and support the gala events that matter — all from one beautifully simple account.'

  return (
    <>
      <Head title={title}>
        <meta head-key="description" name="description" content={description} />
        <meta head-key="og:title" property="og:title" content={title} />
        <meta head-key="og:description" property="og:description" content={description} />
        <meta head-key="og:type" property="og:type" content="website" />
        <meta head-key="og:url" property="og:url" content={`${app_url}/philanthropists`} />
        <meta head-key="twitter:card" name="twitter:card" content="summary_large_image" />
        <meta head-key="twitter:title" name="twitter:title" content={title} />
        <meta head-key="twitter:description" name="twitter:description" content={description} />
      </Head>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden="true">
          <div className="absolute inset-x-0 top-0 h-[140%] bg-[linear-gradient(to_bottom,oklch(0.85_0.11_62/0.55)_0%,oklch(0.91_0.07_72/0.28)_28%,transparent_62%)]" />
          <div className="absolute -top-1/4 -right-[8%] h-[95%] w-[60%] rounded-full bg-[radial-gradient(circle,oklch(0.80_0.15_55/0.45)_0%,transparent_65%)] blur-2xl" />
        </div>
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:gap-10 lg:px-8 lg:py-24">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-primary">
              <span aria-hidden="true">✦</span>
              For philanthropists
            </p>
            <h1 className="mt-5 text-4xl font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">
              The Network for Changemakers.
            </h1>
            <p className="mt-6 max-w-[48ch] text-lg text-foreground text-pretty">
              Gala Season connects you to the organizations, causes, professional networks, and
              fellow philanthropists shaping the events and issues you care about most.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button size="lg" asChild>
                <Link href="/users/sign_up" className="gap-1.5">
                  Create your free account <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/events">Browse upcoming galas</Link>
              </Button>
            </div>
          </div>
          <div className="relative lg:pl-8">
            <PhilanthropistListPreview />
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Everything you need to give well
          </h2>
          <p className="mt-2 text-lg text-foreground">
            Gala Season brings the world of socially responsible events — and the organizations,
            causes, professional networks, and philanthropists behind them — into a single,
            beautifully designed platform.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map(({ icon: Icon, title: heading, description: copy }) => (
            <div
              key={heading}
              className="rounded-2xl bg-card p-6 shadow-sm ring-1 ring-black/5 transition-all duration-200 hover:shadow-lg hover:ring-primary/20"
            >
              <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10">
                <Icon className="size-5 text-primary" />
              </div>
              <h3 className="mt-4 text-lg font-semibold tracking-tight">{heading}</h3>
              <p className="mt-2 text-base text-muted-foreground">{copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Connections */}
      <section className="bg-muted/30">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Stay connected to what moves you
            </h2>
            <p className="mt-2 text-lg text-foreground">
              Philanthropy thrives on relationships. Gala Season makes it effortless to stay close
              to the organizations, causes, networks, and people powering the work.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              {
                icon: Building2,
                label: 'Organizations',
                copy: 'Follow the nonprofits and foundations you support — and never miss their next event, campaign, or call to action.',
              },
              {
                icon: Heart,
                label: 'Causes',
                copy: 'Tag the cause areas closest to your heart and surface the events, hosts, and peers aligned with your mission.',
              },
              {
                icon: Briefcase,
                label: 'Professional networks',
                copy: 'Plug into industry guilds, alumni circles, and affinity groups to discover the events your community is rallying around.',
              },
              {
                icon: Users,
                label: 'Other philanthropists',
                copy: 'Find, follow, and grow alongside other givers — see who is showing up where, and build the room before you walk in.',
              },
            ].map(({ icon: Icon, label, copy }) => (
              <div
                key={label}
                className="rounded-2xl bg-card p-6 shadow-sm ring-1 ring-black/5 transition-all duration-200 hover:shadow-lg hover:ring-primary/20"
              >
                <div className="flex size-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="size-5 text-primary" />
                </div>
                <h3 className="mt-4 text-lg font-semibold tracking-tight">{label}</h3>
                <p className="mt-2 text-base text-muted-foreground">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section>
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Built for the way you actually attend events
            </h2>
            <p className="mt-2 text-lg text-foreground">
              From the first invitation to the post-event tax receipt, Gala Season fits the rhythm
              of philanthropic life.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 gap-x-10 gap-y-8 md:grid-cols-2">
            {features.map(({ title: heading, description: copy }) => (
              <div key={heading} className="flex gap-4">
                <div className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-lg bg-secondary/30">
                  <Calendar className="size-4 text-foreground" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold tracking-tight">{heading}</h3>
                  <p className="mt-1 text-base text-muted-foreground">{copy}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-2xl bg-linear-to-br from-[#F3722B] to-[#F7961E] p-8 text-center sm:p-12">
          <h2 className="text-3xl font-semibold tracking-tight text-white sm:text-4xl">
            Your next event is already on the calendar
          </h2>
          <p className="mt-2 max-w-2xl mx-auto text-white/90 font-medium">
            Join Gala Season free in less than a minute. Discover the events worth showing up for —
            and the causes worth giving to.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              href="/users/sign_up"
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-white px-4 text-base font-semibold text-primary transition-all hover:bg-white/90"
            >
              Sign up free
            </Link>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 bg-white/10 font-semibold text-white backdrop-blur-sm hover:bg-white/20 hover:text-white"
              asChild
            >
              <Link href="/events">Browse Events</Link>
            </Button>
          </div>
        </div>
      </section>
    </>
  )
}
